import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    Autocomplete,
    TextField
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const CompetitorDealsOverview = ({ topLineClass, deals, website }) => {
    console.log('Deals Overview: ', deals)
    const [expandedDeals, setExpandedDeals] = useState({});
    const [selectedCategories, setSelectedCategories] = useState([]);
    
    // Get unique categories and initialize all as selected
    const allCategories = [...new Set(deals.map(deal => deal.category).filter(Boolean))];
    
    useEffect(() => {
        setSelectedCategories(allCategories);
    }, [deals]);

    const handleDealToggle = (dealIndex) => {
        setExpandedDeals(prev => ({
            ...prev,
            [dealIndex]: !prev[dealIndex]
        }));
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return typeof price === 'number' ? `$${price.toFixed(2)}` : price;
    };

    // Filter deals based on selected categories
    const filteredDeals = deals.filter(deal => {
        return selectedCategories.includes(deal.category);
    });

    if (!deals || deals.length === 0) {
        return null;
    }

    return (
        <div className={topLineClass}>
            <div className='box-container Competitor-market-segment'>
                {/* Category Filter */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
                    <Autocomplete
                        multiple
                        options={allCategories}
                        value={selectedCategories}
                        onChange={(event, newValue) => {
                            setSelectedCategories(newValue);
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter Categories"
                                placeholder="Select categories"
                            />
                        )}
                        sx={{ minWidth: 300 }}
                    />
                </Box>
                
                <Box sx={{ width: '100%' }}>
                    {filteredDeals.map((deal, dealIndex) => (
                        deal.description ? (
                            <Accordion 
                                key={dealIndex}
                                expanded={expandedDeals[dealIndex] || false}
                                onChange={() => handleDealToggle(dealIndex)}
                                sx={{ mb: 1, boxShadow: 'none', border: '1px solid #E9EAEB' }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ backgroundColor: '', minHeight: '56px' }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%', pl: 2 }}>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                fontWeight: 600, 
                                                color: '#1976d2',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {deal.name || 'Deal Name Not Available'}
                                        </Typography>
                                        
                                        <Chip 
                                            label={deal.category || 'N/A'} 
                                            size="small" 
                                            color="secondary" 
                                            variant="outlined"
                                        />
                                        
                                        <Typography variant="body1" color="error" sx={{ fontWeight: 600, ml: 'auto' }}>
                                            {formatPrice(deal.price)}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                
                                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                                    {/* Deal Description */}
                                    <Box sx={{ mb: 2, p: 2, pl: 4, backgroundColor: '#f9f9f9', borderRadius: 1, textAlign: 'left' }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'left' }}>
                                            Deal Description:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                                            {deal.description}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Additional Deal Info */}
                                    <Box sx={{ pl: 2 }}>
                                        {deal.validUntil && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Valid Until:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {deal.validUntil}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {deal.terms && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Terms & Conditions:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {deal.terms}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {deal.originalPrice && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Original Price:
                                                </Typography>
                                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                                    {formatPrice(deal.originalPrice)}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {deal.discount && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    Discount:
                                                </Typography>
                                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                                    {deal.discount}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ) : (
                            // Simple row for deals without description
                            <Box 
                                key={dealIndex}
                                sx={{ 
                                    mb: 1, 
                                    p: 2, 
                                    border: '1px solid #E9EAEB', 
                                    borderRadius: 1,
                                    backgroundColor: ''
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pl: 2 }}>
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            fontWeight: 600, 
                                            color: '#1976d2',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {deal.name || 'Deal Name Not Available'}
                                    </Typography>
                                    
                                    <Chip 
                                        label={deal.category || 'N/A'} 
                                        size="small" 
                                        color="secondary" 
                                        variant="outlined"
                                    />
                                    
                                    <Typography variant="body1" color="error" sx={{ fontWeight: 600, ml: 'auto' }}>
                                        {formatPrice(deal.price)}
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    ))}
                </Box>
            </div>
        </div>
    );
};